<script lang="ts" context="module">
  export const projectCommentsQueryKey = (projectId: number) => ['project-comments', projectId];
</script>

<script lang="ts">
  import { client } from '$lib/api/Client';
  import type { ListProjectCommentsResponse } from '$shared/api';
  import { type HackathonProject } from '$shared/lib';
  import { createInfiniteQuery } from '@tanstack/svelte-query';
  import type { User } from 'src/routes/(app)/+layout.server';
  import InfiniteScroll from '$lib/components/InfiniteScroll.svelte';
  import AddComment from '$lib/components/reusable/projects/comments/AddComment.svelte';
  import { cn } from '$lib/utils/misc';
  import DoubleBubbleIcon from '$icons/messages/DoubleBubbleIcon.svelte';
  import CommentSkeleton from '$lib/components/reusable/projects/comments/CommentSkeleton.svelte';
  import Comment from '$lib/components/reusable/projects/comments/Comment.svelte';

  export let project: HackathonProject;
  export let user: User | null;

  const LIMIT = 10;

  const commentsQuery = createInfiniteQuery({
    queryKey: projectCommentsQueryKey(project.id),
    queryFn: ({ pageParam }: { pageParam: number }) =>
      client.projects.listComments({ projectId: project.id, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: ListProjectCommentsResponse) =>
      lastPage.hasMore ? lastPage.offset + LIMIT : undefined,
  });

  async function loadMoreTopLevelComments() {
    if ($commentsQuery.hasNextPage) {
      await $commentsQuery.fetchNextPage();
    }
  }

  let forceCloseReplyInputs = false;

  // This let's us get rid of some awkward behavior where the "reply to comment"
  // input box stays open on their own comment whenever the user makes a new comment
  function handleNewComment() {
    forceCloseReplyInputs = true;
    setTimeout(() => {
      forceCloseReplyInputs = false;
    }, 0);
  }
</script>

<div class={cn('w-full', $$props.class)}>
  <AddComment {project} currentUser={user} onCommentSuccess={handleNewComment} />
  {#if $commentsQuery.isLoading}
    <div class="mt-8 flex flex-col gap-y-4">
      {#each Array(3) as _}
        <CommentSkeleton />
      {/each}
    </div>
  {:else if $commentsQuery.isSuccess}
    {#if $commentsQuery.data.pages[0].comments.length === 0}
      <div class="flex flex-col items-center gap-6 py-20 text-center">
        <DoubleBubbleIcon class="h-8 w-8 text-white" />
        <p class="text-gray-dark-9">No comments yet. Be the first to share your thoughts!</p>
      </div>
    {:else}
      <div class="mt-8 flex flex-col gap-y-4">
        {#each $commentsQuery.data.pages.flatMap((page) => page.comments) as comment}
          <Comment {comment} currentUser={user} forceCloseReply={forceCloseReplyInputs} />
        {/each}
      </div>
      <InfiniteScroll
        bind:hasMore={$commentsQuery.hasNextPage}
        bind:loadingMore={$commentsQuery.isFetchingNextPage}
        on:load-more={async () => {
          await loadMoreTopLevelComments();
        }}
      />
    {/if}
  {/if}
</div>
